import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Info } from "lucide-react";
interface TrailerItem {
  id: string;
  title: string;
  image: string;
  alt: string;
  category: "towable" | "commercial";
  specs: {
    exterior: string;
    interior: string;
    electrical?: string;
    temperature?: string;
    options?: string;
    notes?: string;
  };
}
const trailers: TrailerItem[] = [{
  id: "trailer-6x8",
  title: "6×8 Refrigerated & Freezer Trailer",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png",
  alt: "6 by 8 foot mobile refrigerated and freezer trailer",
  category: "towable",
  specs: {
    exterior: "144″ L × 92″ W × 92″ H",
    interior: "89.5″ L × 65″ W × 62″ H",
    options: "Power, temperature, equipment, availability, and suitability are confirmed with each quote. Optional generator and shelving may be available for eligible smaller trailers."
  }
}, {
  id: "trailer-7x12",
  title: "7×12 Refrigerated & Freezer Trailer",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_47_20-PM-1.png",
  alt: "7 by 12 foot mobile refrigerated and freezer trailer",
  category: "towable",
  specs: {
    exterior: "192″ L × 102″ W × 98″ H",
    interior: "144″ L × 74″ W × 72″ H",
    options: "Power, temperature, equipment, availability, and suitability are confirmed with each quote. Optional generator and shelving may be available for eligible smaller trailers."
  }
}, {
  id: "trailer-7x16",
  title: "7×16 Refrigerated & Freezer Trailer",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png",
  alt: "7 by 16 foot mobile refrigerated and freezer trailer",
  category: "towable",
  specs: {
    exterior: "240″ L × 102″ W × 92″ H",
    interior: "192″ L × 74″ W × 72″ H",
    options: "Power, temperature, equipment, availability, and suitability are confirmed with each quote. Optional generator and shelving may be available for eligible smaller trailers."
  }
}, {
  id: "trailer-53ft",
  title: "53-Foot Reefer Trailer",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/FF-Reefer-trailer.png",
  alt: "53-Foot commercial refrigerated semi-trailer",
  category: "commercial",
  specs: {
    exterior: "Typical exterior 53′ L × 102.36″ W × 13′ 6″ H",
    interior: "Typical interior approximately 52′ 2″ L × 97.4″ W × 105.5″ H (typical manufacturer dimensions)",
    notes: "Availability, specifications, temperature requirements, power/fuel needs and suitability for the 53-foot reefer are confirmed individually and are NOT the same as the smaller trailers' specifications — do not apply smaller trailers' electrical, temperature or equipment details to this unit."
  }
}];
export default function TrailerLineup() {
  const towableTrailers = trailers.filter(t => t.category === "towable");
  const commercialTrailer = trailers.find(t => t.category === "commercial");
  return <section id="trailerlineup" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Section Header */}
        <div className="max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
            Refrigerated & Freezer Trailer Lineup
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground">
            Explore mobile refrigerated and freezer trailer options, including 6×8, 7×12, and 7×16 towable trailers plus a 53-foot reefer option. Availability, specifications, requirements, and suitability are confirmed with each quote.
          </p>
        </div>

        {/* Towable Mobile Units Group */}
        <div className="space-y-6">
          <div className="border-b border-border pb-3 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-foreground tracking-tight">
              Standard towable cold storage trailers
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {towableTrailers.map((trailer, i) => <Card key={trailer.id} data-index={i} className="bg-card text-card-foreground border border-border rounded-xl shadow-xs flex flex-col justify-between overflow-hidden transition-all duration-200 hover:shadow-md">
                <div>
                  <div className="bg-muted/40 p-6 flex items-center justify-center border-b border-border/80 aspect-[16/10] overflow-hidden">
                    <img src={trailer.image} data-wvc-srcset={`${trailer.image}-wvc-srcset`} data-wvc-sizes={`${trailer.image}-wvc-sizes`} alt={trailer.alt} loading="lazy" decoding="async" className="max-h-48 w-auto object-contain transition-transform duration-300 hover:scale-105" />
                  </div>

                  <CardHeader className="pt-6 pb-2">
                    <CardTitle className="text-xl font-semibold text-card-foreground leading-snug">
                      {trailer.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-2 text-sm">
                    {/* Dimensions */}
                    <div className="rounded-lg bg-muted/30 p-3 space-y-1.5 border border-border/50">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Dimensions
                      </div>
                      <div className="text-foreground">
                        <span className="font-medium text-muted-foreground">Exterior:</span>{" "}
                        {trailer.specs.exterior}
                      </div>
                      <div className="text-foreground">
                        <span className="font-medium text-muted-foreground">Interior:</span>{" "}
                        {trailer.specs.interior}
                      </div>
                    </div>

                    {/* Operational specs */}
                    <div className="space-y-2 text-muted-foreground text-sm leading-relaxed">
                      {trailer.specs.options && <div className="flex items-start gap-2">
                          <Check className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{trailer.specs.options}</span>
                        </div>}
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 pb-6">
                  <Button asChild className="w-full">
                    <Link to="/ContactRequestAQuotePage">Request a quote</Link>
                  </Button>
                </CardFooter>
              </Card>)}
          </div>
        </div>

        {/* Commercial Transport Reefer Group */}
        {commercialTrailer && <div className="space-y-6 pt-4">
            <div className="border-b border-border pb-3 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground tracking-tight">
                53-Foot Reefer Option
              </h3>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Individual Confirmation
              </span>
            </div>

            <Card className="bg-card text-card-foreground border border-border rounded-xl shadow-xs overflow-hidden transition-all duration-200 hover:shadow-md">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-center">
                <div className="lg:col-span-5 bg-muted/40 p-8 flex items-center justify-center border-b lg:border-b-0 lg:border-r border-border min-h-[260px]">
                  <img src={commercialTrailer.image} data-wvc-srcset={`${commercialTrailer.image}-wvc-srcset`} data-wvc-sizes={`${commercialTrailer.image}-wvc-sizes`} alt={commercialTrailer.alt} loading="lazy" decoding="async" className="max-h-56 w-auto object-contain transition-transform duration-300 hover:scale-105" />
                </div>

                <div className="lg:col-span-7 flex flex-col justify-between p-6 sm:p-8 space-y-6">
                  <div>
                    <CardHeader className="p-0 pb-3">
                      <CardTitle className="text-2xl font-semibold text-card-foreground">
                        {commercialTrailer.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0 space-y-4 pt-1">
                      {/* Dimensions block */}
                      <div className="rounded-lg bg-muted/30 p-3.5 space-y-1.5 border border-border/50 text-sm">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Manufacturer dimensions
                        </div>
                        <div className="text-foreground">
                          <span className="font-medium text-muted-foreground">Exterior:</span>{" "}
                          {commercialTrailer.specs.exterior}
                        </div>
                        <div className="text-foreground">
                          <span className="font-medium text-muted-foreground">Interior:</span>{" "}
                          {commercialTrailer.specs.interior}
                        </div>
                      </div>

                      {/* Explicit operational disclaimer note */}
                      {commercialTrailer.specs.notes && <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-4 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          <Info className="size-5 text-primary shrink-0 mt-0.5" />
                          <p>{commercialTrailer.specs.notes}</p>
                        </div>}
                    </CardContent>
                  </div>

                  <CardFooter className="p-0 pt-2">
                    <Button asChild size="lg" className="w-full sm:w-auto">
                      <Link to="/ContactRequestAQuotePage">Request a Quote</Link>
                    </Button>
                  </CardFooter>
                </div>
              </div>
            </Card>
          </div>}
      </div>
    </section>;
}